"use client";

// ─────────────────────────────────────────────────────────────────────────
// "Where was the buyer before they came to buy?"
//
// Every purchase surface needs a `returnTo`, and until now each one guessed.
// The pricing page sent buyers back to /pricing — the price list they had just
// accepted — the plan cards sent them to /projects, and the trial conversion
// hardcoded /settings/billing, which is how a user who clicked Upgrade inside
// the builder finished in Settings with no way back to what they were doing.
// Three answers to one question, none of them the page the user actually left.
//
// The page they left is a fact about navigation, not about billing, so it is
// recorded once (<PurchaseOriginRecorder/>, mounted in the root layout) and
// read here. No billing call site has to be told where the user came from, and
// no CTA has to append a `?returnTo=` — which matters because ~20 places in the
// app route to /pricing, and a rule that has to be remembered at twenty call
// sites is a rule that will be missed at some of them.
//
// ── WHY THE PATH ONLY, NEVER THE QUERY ──
// A recorded path is replayed as a redirect after money changes hands, and some
// of this app's query strings are INSTRUCTIONS, not state: `/project/<id>
// ?prompt=…` starts a real build, `?upgraded=true` re-fires a confirmation.
// Replaying one would make a completed purchase do something the user never
// asked for, so the query is dropped at the point of recording.
//
// sessionStorage, not localStorage: this describes one tab's journey to a
// checkout and back. In localStorage it would leak across tabs and outlive the
// trip — the same reasoning as lib/billing/checkout-intent-stash.ts.
// ─────────────────────────────────────────────────────────────────────────

const KEY = "panda:purchase-origin";

/**
 * A checkout round-trip is minutes: card hunting, a 3DS detour, a phone unlock.
 * Past that, "where they came from" is a guess about a session they have since
 * left, and /projects is the better answer than a stale page.
 */
const TTL_MS = 60 * 60 * 1000;

/**
 * Surfaces a buyer passes THROUGH on the way to paying: the price list, the
 * checkout itself, and the auth screens a signed-out visitor is diverted to
 * mid-purchase. Landing someone back on one of these after they have paid is
 * the bug — /pricing hands them the same CTA they just bought through — so
 * none of them is ever recorded as an origin or offered as a destination.
 *
 * /settings/billing is deliberately NOT here. It sells, but it is also where
 * what you just bought is shown, so it is a perfectly good place to come from
 * and to stay on.
 *
 * Matched on the exact path or a path segment, never a bare `startsWith`, so
 * "/pricing-experiment" cannot be mistaken for "/pricing" — the same rule
 * SELF_HANDLED_RETURN_PATHS follows in lib/billing/checkout-return.ts.
 */
const SELLING_PATHS = [
  "/pricing",
  "/checkout",
  "/login",
  "/signup",
  "/auth",
] as const;

/** True for a same-origin path we may redirect a buyer to. `//host` is not one:
 *  the browser reads it as protocol-relative and leaves the site. */
export function isSafeReturnPath(path: unknown): path is string {
  return (
    typeof path === "string" && path.startsWith("/") && !path.startsWith("//")
  );
}

/** True when `path` is a buying surface rather than somewhere to return to. */
export function isSellingSurface(path: string): boolean {
  return SELLING_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Record the page the user is on, so a purchase started later can come back to
 *  it. Callers pass a bare pathname; anything unsafe or selling is ignored. */
export function rememberPurchaseOrigin(path: string): void {
  if (!isSafeReturnPath(path) || isSellingSurface(path)) return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path, timestamp: Date.now() }),
    );
  } catch {
    // Private windows, blocked site data. A purchase must still complete — the
    // buyer just lands on the fallback.
  }
}

/** The recorded origin, or null if there is none, it is stale, or unreadable. */
export function readPurchaseOrigin(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { path?: unknown; timestamp?: unknown };
    if (
      !isSafeReturnPath(parsed.path) ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > TTL_MS
    ) {
      return null;
    }
    return parsed.path;
  } catch {
    return null;
  }
}

/**
 * Where to send this buyer when their purchase completes.
 *
 * In order:
 *
 *   1. An explicit `?returnTo=` on the current URL. A caller that KNOWS always
 *      outranks anything inferred.
 *   2. The page they are on right now — because for most purchases "back where
 *      they came from" IS this page: a credit gate in the builder, a plan card
 *      in Settings. Sending those anywhere else would be the same bug pointed
 *      in a new direction.
 *   3. The page before this one, when this one exists to sell (/pricing,
 *      /checkout). Returning a buyer to the price list they just accepted is
 *      what made this worth fixing.
 *   4. /projects — the signed-in home, for a buyer who arrived at /pricing cold
 *      and has no history to go back to.
 *
 * Read at CLICK time, not at render: on a selling surface the recording belongs
 * to the page BEFORE it, and a component that resolved this on mount would pin
 * whatever happened to be stored first.
 */
export function resolvePurchaseReturnPath(fallback = "/projects"): string {
  if (typeof window === "undefined") return fallback;
  const explicit = new URLSearchParams(window.location.search).get("returnTo");
  if (isSafeReturnPath(explicit)) return explicit;

  const here = window.location.pathname;
  // The query is deliberately dropped: `/project/<id>?prompt=…` starts a real
  // build, and replaying that after a payment would build something nobody
  // asked for. See the header.
  if (isSafeReturnPath(here) && !isSellingSurface(here)) return here;

  return readPurchaseOrigin() ?? fallback;
}
